/* eslint-disable @typescript-eslint/no-explicit-any */
import type { AxiosResponse } from 'axios';
import { call, delay, put, takeLatest } from 'redux-saga/effects';

import api from '../../api/api';
import { errorHandling } from '../../api/errorHandling';
import { mapSuccessResponseToState } from '../../api/responseHandling';
import * as commonSagas from '../_common/commonSagas';
import { getApiVersion } from '../apiVersion';
import * as destPointSlice from './destPointSlice';

// The key mapFormToPatch('create') parks the detail rows under — it is also the
// only path the patch endpoint special-cases for details.
const DETAILS_KEY = 'DestPointDetails';

// DestPointCreateUpdateDTOV2 has no details member, so rows sent inside the
// create body are silently dropped on deserialization. A new destpoint's
// details therefore need two phases: create, then patch the returned id.
export function* createItem(props: any) {
	const { table, data } = props.payload;
	const { [DETAILS_KEY]: detailRows, ...body } = (data || {}) as Record<
		string,
		unknown
	>;
	const rows: any[] = Array.isArray(detailRows) ? detailRows : [];

	try {
		const version = getApiVersion(table, 'createItem');
		const { path } = commonSagas.buildTablePath(table, version);
		const response: AxiosResponse<any> = yield call(
			api.post,
			`${path}/create`,
			body,
		);

		let detailResponse: AxiosResponse<any> | null = null;
		let detailError: unknown = null;
		const createdId = (response?.data as { Id?: number })?.Id;
		if (rows.length && createdId) {
			try {
				detailResponse = yield call(
					api.patch,
					`${path}/patch/${createdId}`,
					rows.map((value) => ({ op: 'add', path: `/${DETAILS_KEY}`, value })),
				);
			} catch (error: unknown) {
				detailError = error;
			}
		}

		// The destpoint itself exists even when the detail patch failed, so the
		// success action must still fire — it is what closes the modal and
		// refetches the grid. The patch error is surfaced right after so the user
		// learns the rows were not saved instead of the record silently losing
		// them. The patch response is preferred because it is the fuller item
		// (the controller answers a patch with the whole reloaded DTO).
		yield put(
			destPointSlice.createItemSuccess(
				mapSuccessResponseToState(detailResponse || response, 'created'),
			),
		);
		yield delay(0);
		yield put(destPointSlice.createItemFinish());

		if (detailError) {
			yield* errorHandling(detailError, destPointSlice.updateItemFailure);
		}
	} catch (error: unknown) {
		yield* errorHandling(error, destPointSlice.createItemFailure);
	}
}

export default function* watchDestPointSaga() {
	yield takeLatest(destPointSlice.getTableStart.type, commonSagas.getTable);
	yield takeLatest(destPointSlice.getItemStart.type, commonSagas.getItem);
	// details need the two-phase create; the update flow stays generic because
	// its detail ops ride along in the same PATCH as every other change
	yield takeLatest(destPointSlice.createItemStart.type, createItem);
	yield takeLatest(destPointSlice.updateItemStart.type, commonSagas.updateItem);
	yield takeLatest(
		destPointSlice.deleteItemsStart.type,
		commonSagas.deleteItems,
	);
	yield takeLatest(destPointSlice.deleteItemStart.type, commonSagas.deleteItem);
	yield takeLatest(
		destPointSlice.getTableStructureStart.type,
		commonSagas.getTableStructure,
	);
	yield takeLatest(
		destPointSlice.getItemDocumentsStart.type,
		commonSagas.getItemDocuments,
	);
	yield takeLatest(
		destPointSlice.uploadItemDocumentsStart.type,
		commonSagas.uploadItemDocuments,
	);
	yield takeLatest(
		destPointSlice.deleteItemDocumentStart.type,
		commonSagas.deleteItemDocument,
	);
	yield takeLatest(
		destPointSlice.getTableExcelStart.type,
		commonSagas.getTableExcel,
	);
}
